import { useEffect, useState } from "react";
import LoggedInLayout from "../../layout/LoggedInLayout";
import {
  Box,
  IconButton,
  Typography,
  Button,
  Paper,
  Divider,
  CircularProgress,
  Dialog,
  DialogContent,
} from "@mui/material";
import {
  ArrowBack,
  Download,
  Visibility,
  ChatBubbleOutline,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import RecommendedCareer from "../Dashboard/RecommendedCareer";
import httpcommon from "../../helper/httpcommon";
import { useToast } from "../../hook/useToast";
import PropTypes from "prop-types";

const styles = {
  header: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "rgba(243, 243, 245, 1)",
    height: "46px",
    borderRadius: "6px",
    paddingLeft: "12px",
    marginLeft: 0,
    marginBottom: "24px",
    width: "100%",
    boxSizing: "border-box",
  },

  backBtn: {
    padding: 0,
    marginRight: "8px",
    width: "28px",
    height: "28px",
    backgroundColor: "#fff",
    borderRadius: "50%",
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
  },

  backIcon: {
    fontSize: "22px",
    color: "rgba(157, 158, 158, 1)",
  },

  pageHeading: {
    fontSize: "15px",
    fontWeight: 500,
    color: "#231F20",
  },

  reportsWrapper: {
    width: { xs: "100%", sm: "90%" },
    margin: "0 auto",
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: { xs: "16px", sm: "24px" },
  },

  cardContainer: {
    flex: { xs: "1 1 100%", sm: "1 1 30%" },
    minWidth: { xs: 0, sm: "280px" },
    display: "flex",
    justifyContent: "center",
  },

  activeCard: {
    backgroundColor: "#B7D3D9",
    borderRadius: "14px",
    padding: "10px 0",
    width: { xs: "100%", sm: "250px" },
    maxWidth: "320px",
    height: "400px",
    display: "flex",
    flexDirection: "column",
    textAlign: "center",
    justifyContent: "center",
    alignItems: "center",
  },

  disabledCard: {
    backgroundColor: "#E6E6E6",
    borderRadius: "14px",
    padding: "10px 0",
    width: { xs: "100%", sm: "250px" },
    maxWidth: "320px",
    height: "400px",
    display: "flex",
    flexDirection: "column",
    textAlign: "center",
    justifyContent: "center",
    alignItems: "center",
  },

  reportImage: {
    width: "160px",
    height: "230px",
    objectFit: "cover",
    borderRadius: "6px",
    marginTop: "10px",
    marginBottom: "14px",
  },

  buttonRow: {
    display: "flex",
    gap: "10px",
    marginBottom: "10px",
  },

  smallBtn: {
    textTransform: "none",
    fontSize: "12px",
    borderRadius: "20px",
    padding: "4px 14px",
  },
};

// ✅ ADDED: format uploaded_at date
const formatDate = (isoString) => {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const ReportCard = ({ title, active, document, onView, onPreview }) => {

  // ✅ ADDED: download handler
  const handleDownload = async () => {
    if (!document?.file_url) return;

    const link = window.document.createElement("a");
    link.href = document.file_url;
    link.download=document.filename
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.click();
  }

  return (
    <Paper
      elevation={0}
      sx={active ? styles.activeCard : styles.disabledCard}
    >
      <Typography fontWeight={500} fontSize={14} mt={1} mb={1}>
        {title}
      </Typography>
      <Divider
        sx={{
          width: "100%",
          borderWidth: "2px",
          borderColor: "rgba(255,255,255,0.7)",
          my: 1,
        }}
      />
      {/* ✅ ADDED: show generated date from API */}
      <Typography fontSize={12} color="#000" fontWeight={500}>
        Generated Date: {active && document ? formatDate(document.uploaded_at) : "—"}
      </Typography>

      <Box
        component="img"
        src={active ? "/assets/newImages/report-background.png" : "/assets/newImages/report-background-disabled.png"}
        alt="report preview"
        sx={styles.reportImage}
      />

      <Box sx={styles.buttonRow}>
        <Button
          variant="outlined"
          startIcon={<Download />}
          sx={styles.smallBtn}
          onClick={handleDownload} // ✅ ADDED
        >
          Download
        </Button>

        <Button
          variant="outlined"
          startIcon={<Visibility />}
          sx={styles.smallBtn}
          onClick={() => onView && onView(document?.file_url)} // ✅ ADDED
        >
          View
        </Button>
      </Box>

      <Button
        variant="text"
        startIcon={<ChatBubbleOutline />}
        sx={{ textTransform: "none", fontSize: "12px" }}
        onClick={() => onPreview && onPreview(document?.studentcareerreport_id)} // ✅ ADDED
      >
        Preview
      </Button>
    </Paper>
  );
};

const CARD_TITLES = ["Latest", "Previous", "Earlier"]; // ✅ ADDED

ReportCard.propTypes = {
  title: PropTypes.string.isRequired,
  active: PropTypes.bool.isRequired,
  document: PropTypes.shape({
    file_url: PropTypes.string,
    filename: PropTypes.string,
    uploaded_at: PropTypes.string,
    studentcareerreport_id: PropTypes.number,
  }),
  onView: PropTypes.func.isRequired,
  onPreview: PropTypes.func.isRequired,
};

const Reports = ({ studentUserId, studentName, childSelector }) => {
  const navigate = useNavigate();
  const { showToast } = useToast(); // ✅ ADDED

  // ✅ ADDED
  const [documents, setDocuments] = useState({ documents: [], careers: [] });
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewData, setPreviewData] = useState({});

  // ✅ ADDED
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        setDocuments({ documents: [], careers: [] });
        setPreviewUrl(null);
        setPreviewData({});
        const response = await httpcommon.get("school/student/documents/", {
          params: studentUserId ? { student_user_id: studentUserId } : undefined,
        });
        if (response.status === 200) {
          setDocuments({
            documents: response.data?.documents || [],
            careers: response.data?.careers || [],
          });
          setPreviewData(response.data?.careers?.[0]?.career_info || {}); // Set initial preview data
        }
      } catch (error) {
        showToast(error?.response?.data?.error || "Error fetching documents", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchDocuments();
  }, [showToast, studentUserId]);

  const reportDocuments = documents?.documents || [];
  const reportCareers = documents?.careers || [];

  return (
    <LoggedInLayout>
      {/* Header */}
      <Box sx={{ m: { xs: 1, sm: 2 }, overflowX: "hidden" }}>
        {childSelector}
        <Box sx={styles.header}>
          <IconButton onClick={() => navigate(-1)} sx={styles.backBtn}>
            <ArrowBack sx={styles.backIcon} />
          </IconButton>

          <Typography sx={styles.pageHeading}>
            {studentName ? `${studentName}'s Reports` : "Get Report"}
          </Typography>
        </Box>

        {/* ✅ ADDED: loading state */}
        {loading ? (
          <Box display="flex" justifyContent="center" mt={6}>
            <CircularProgress />
          </Box>
        ) : (
          /* Reports Section - same layout, now driven by API */
          <Box sx={styles.reportsWrapper}>
            {CARD_TITLES.map((title, index) => (
              <Box key={index} sx={styles.cardContainer}>
                <ReportCard
                  title={title}
                  active={!!reportDocuments[index]}
                  document={reportDocuments[index] || null}
                  onView={(url) => setPreviewUrl(url)} // ✅ ADDED: open preview dialog
                  onPreview={(studentcareerreport_id) => {
                    if (!studentcareerreport_id) return;
                    const selectedCareer = reportCareers.find(
                      each => each.id == studentcareerreport_id
                    );
                    setPreviewData(selectedCareer?.career_info || {})
                  }}
                />
              </Box>
            ))}
          </Box>
        )}

        {/* Recommended Career */}
        <Box sx={{
          mt: { xs: 4, sm: 8 },
          mb: { xs: 4, sm: 8 },
          backgroundColor: "#F3F3F5",
          p: { xs: 1.5, sm: 4 },
          pb: { xs: 4, sm: 10 },
          borderRadius: 2,
          overflowX: "hidden",
          boxSizing: "border-box",
        }}>
          <RecommendedCareer showMilestoneLayout={false} career={previewData} />
        </Box>
      </Box>

      {/* ✅ ADDED: PDF preview dialog */}
      <Dialog
        open={!!previewUrl}
        onClose={() => setPreviewUrl(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ padding: 0, height: "85vh" }}>
          <iframe
            src={previewUrl}
            width="100%"
            height="100%"
            style={{ border: "none" }}
            title="Report Preview"
          />
        </DialogContent>
      </Dialog>
    </LoggedInLayout>
  );
};

Reports.propTypes = {
  studentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  studentName: PropTypes.string,
  childSelector: PropTypes.node,
};

export default Reports;
